import React, { useEffect, useMemo, useState } from 'react';
import {
  listCachedProtocolUrls,
  protocolStorageSupported,
  pruneProtocolPdfs,
  readProtocolManifest,
  readProtocolPdf,
  writeProtocolManifest,
  writeProtocolPdf,
} from './protocolOfflineStore';
import ProtocolDocumentViewer from './ProtocolDocumentViewer';
import './sac-county-protocols.css';

const MANIFEST_URL = '/api/sac-protocols';
const SOURCE_LABEL = 'Sacramento County EMS Agency';
const SOURCE_PAGE = 'https://dhs.saccounty.gov/us/en/public-health/services/emergency-medical-services/resources-library/policies--procedures--and-protocols.html';
let backgroundSyncPromise = null;

function proxyUrl(url) {
  return `/api/sac-protocol-pdf?src=${encodeURIComponent(url)}`;
}

function onlineNow() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function searchText(item) {
  return [item.number, item.revision, item.title, item.displayTitle, item.category]
    .map(value => String(value || '').toLowerCase())
    .join(' ');
}

function displayProtocolNumber(item) {
  return String(item?.number || item?.revision || '').trim();
}

function protocolNumericValue(item) {
  const source = [item?.number, item?.revision, item?.displayTitle, item?.title]
    .map(value => String(value || ''))
    .join(' ');
  const match = source.match(/\b(\d{4})\b/);
  return match ? Number(match[1]) : null;
}

function compareProtocolPriority(a, b) {
  const aNumber = protocolNumericValue(a);
  const bNumber = protocolNumericValue(b);
  const group = value => value >= 8000 && value < 9000 ? 0 : value >= 9000 && value < 10000 ? 1 : 2;
  const groupDiff = group(aNumber) - group(bNumber);
  if (groupDiff) return groupDiff;
  if (aNumber != null && bNumber != null && aNumber !== bNumber) return aNumber - bNumber;
  if (aNumber != null && bNumber == null) return -1;
  if (aNumber == null && bNumber != null) return 1;
  return String(a?.title || a?.displayTitle || '').localeCompare(String(b?.title || b?.displayTitle || ''), undefined, { numeric: true, sensitivity: 'base' });
}

function formatCheckedAt(value) {
  const parsed = Date.parse(value || '');
  if (!Number.isFinite(parsed)) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(parsed));
}

async function fetchAndCachePdf(protocol) {
  const existing = await readProtocolPdf(protocol.url).catch(() => null);
  if (existing?.blob) return { cached: true, downloaded: false };
  const response = await fetch(proxyUrl(protocol.url), { cache: 'no-store' });
  if (!response.ok) throw new Error(`Unable to cache ${displayProtocolNumber(protocol) || protocol.title}.`);
  const blob = await response.blob();
  if (!String(blob.type || '').toLowerCase().includes('pdf')) throw new Error('The official source did not return a PDF.');
  await writeProtocolPdf(protocol, blob);
  return { cached: true, downloaded: true };
}

function syncLibrary(protocols, onProgress) {
  if (backgroundSyncPromise) return backgroundSyncPromise;
  const items = Array.isArray(protocols) ? protocols : [];
  backgroundSyncPromise = (async () => {
    let completed = 0;
    let downloaded = 0;
    let failed = 0;
    const queue = [...items];
    const worker = async () => {
      while (queue.length) {
        const protocol = queue.shift();
        try {
          const result = await fetchAndCachePdf(protocol);
          if (result.downloaded) downloaded += 1;
        } catch (_) {
          failed += 1;
        } finally {
          completed += 1;
          onProgress?.({ total: items.length, completed, downloaded, failed });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, Math.max(1, items.length)) }, worker));
    await pruneProtocolPdfs(items.map(item => item.url)).catch(() => {});
    return { total: items.length, completed, downloaded, failed };
  })().finally(() => { backgroundSyncPromise = null; });
  return backgroundSyncPromise;
}

export default function SacCountyProtocols({ offline = false }) {
  const [manifest, setManifest] = useState(null);
  const [status, setStatus] = useState({ state: 'loading', message: 'Loading locally saved protocol index…' });
  const [cachedUrls, setCachedUrls] = useState(new Set());
  const [sync, setSync] = useState({ state: 'idle', total: 0, completed: 0, downloaded: 0, failed: 0 });
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [viewer, setViewer] = useState(null);
  const [viewerError, setViewerError] = useState('');

  const refreshCachedUrls = async () => {
    const urls = await listCachedProtocolUrls().catch(() => []);
    setCachedUrls(new Set(urls));
    return urls;
  };

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      const cachedManifest = await readProtocolManifest().catch(() => null);
      if (active && cachedManifest?.protocols?.length) {
        setManifest(cachedManifest);
        setStatus({ state: onlineNow() ? 'checking' : 'offline', message: onlineNow() ? 'Checking Sacramento County for updates…' : 'Using the last saved Sacramento County protocol index.' });
      }
      await refreshCachedUrls();

      if (!onlineNow()) {
        if (active && !cachedManifest?.protocols?.length) setStatus({ state: 'error', message: 'No protocol index has been saved on this device yet. Open Protocols once while online to prepare offline access.' });
        return;
      }

      try {
        const response = await fetch(MANIFEST_URL, { cache: 'no-store' });
        const live = await response.json().catch(() => ({}));
        if (!response.ok || !live?.protocols?.length) throw new Error(live?.error || 'Unable to load the Sacramento County protocol index.');
        await writeProtocolManifest(live);
        if (!active) return;
        setManifest(live);
        setStatus({ state: 'ready', message: `Official index checked ${formatCheckedAt(live.checkedAt)}.` });
        setSync({ state: 'syncing', total: live.protocols.length, completed: 0, downloaded: 0, failed: 0 });
        syncLibrary(live.protocols, progress => {
          if (active) setSync({ state: 'syncing', ...progress });
        }).then(result => {
          if (!active) return;
          setSync({ state: result.failed ? 'partial' : 'ready', ...result });
          refreshCachedUrls();
        });
      } catch (error) {
        if (!active) return;
        if (cachedManifest?.protocols?.length) setStatus({ state: 'offline', message: `County update check unavailable — using saved index. ${error?.message || ''}`.trim() });
        else setStatus({ state: 'error', message: error?.message || 'Unable to load Sacramento County protocols.' });
      }
    };
    initialize();
    return () => { active = false; };
  }, []);

  const categories = useMemo(() => {
    const values = [...new Set((manifest?.protocols || []).map(item => item.category).filter(Boolean))];
    return ['All', ...values];
  }, [manifest]);

  const terms = useMemo(() => query.toLowerCase().trim().split(/\s+/).filter(Boolean), [query]);
  const results = useMemo(() => {
    return (manifest?.protocols || []).filter(item => {
      if (category !== 'All' && item.category !== category) return false;
      if (!terms.length) return true;
      const haystack = searchText(item);
      return terms.every(term => haystack.includes(term));
    }).sort(compareProtocolPriority);
  }, [manifest, category, terms]);

  const openProtocol = async protocol => {
    setViewerError('');
    let record = await readProtocolPdf(protocol.url).catch(() => null);
    if (!record?.blob && onlineNow()) {
      try {
        await fetchAndCachePdf(protocol);
        record = await readProtocolPdf(protocol.url).catch(() => null);
        refreshCachedUrls();
      } catch (error) {
        setViewerError(error?.message || 'Unable to retrieve this protocol.');
      }
    }
    if (!record?.blob) {
      setViewer({ protocol, blob: null });
      setViewerError(onlineNow() ? 'This protocol could not be downloaded from the official County source.' : 'This protocol has not finished caching on this device. Reopen Protocols with service to complete the offline library.');
      return;
    }
    setViewer({ protocol, blob: record.blob });
  };

  const closeViewer = () => {
    setViewer(null);
    setViewerError('');
  };

  const manualSync = async () => {
    if (!manifest?.protocols?.length || !onlineNow()) return;
    setSync({ state: 'syncing', total: manifest.protocols.length, completed: 0, downloaded: 0, failed: 0 });
    const result = await syncLibrary(manifest.protocols, progress => setSync({ state: 'syncing', ...progress }));
    setSync({ state: result.failed ? 'partial' : 'ready', ...result });
    await refreshCachedUrls();
  };

  if (viewer) {
    const protocol = viewer.protocol;
    return (
      <div className="sac-protocol-viewer">
        <div className="sac-protocol-viewer-head">
          <div>
            <span className="sac-protocol-kicker">OFFICIAL SACRAMENTO COUNTY EMS PROTOCOL</span>
            <h3>{displayProtocolNumber(protocol) ? `${displayProtocolNumber(protocol)} · ` : ''}{protocol.title}</h3>
            <p>{protocol.category}{protocol.revision && protocol.revision !== protocol.number ? ` · File revision ${protocol.revision}` : ''}</p>
          </div>
          <button type="button" onClick={closeViewer}>← Protocol List</button>
        </div>
        {viewerError && <div className="sac-protocol-alert">{viewerError}</div>}
        {viewer.blob ? (
          <ProtocolDocumentViewer blob={viewer.blob} title={`${protocol.number || ''} ${protocol.title}`.trim()} />
        ) : null}
        <div className="sac-protocol-viewer-foot">
          <span>Rendered in the app from the unaltered official Sacramento County EMS PDF cached on this device.</span>
          {onlineNow() && <a href={protocol.url} target="_blank" rel="noreferrer">Open original County PDF ↗</a>}
        </div>
      </div>
    );
  }

  const total = manifest?.protocols?.length || 0;
  const cachedCount = manifest?.protocols?.filter(item => cachedUrls.has(item.url)).length || 0;
  const syncing = sync.state === 'syncing';

  return (
    <div className="sac-protocol-root">
      <div className="sac-protocol-meta">
        <div>
          <span className="sac-protocol-kicker">SACRAMENTO COUNTY EMS · {offline ? 'OFFLINE FIELD REFERENCE' : 'OFFICIAL PROTOCOL LIBRARY'}</span>
          <h3>Policies, procedures & protocols</h3>
          <p>Search the official County library by protocol number, name, revision or category. Documents are stored locally for field use and refreshed from the County source whenever this library is opened with service.</p>
        </div>
        <span className={`sac-protocol-status ${cachedCount === total && total ? 'ready' : ''}`}>{cachedCount}/{total || '—'} offline</span>
      </div>

      <div className="sac-protocol-source">
        <div>
          <strong>{SOURCE_LABEL}</strong>
          <span>{status.message}</span>
        </div>
        {onlineNow() && <a href={SOURCE_PAGE} target="_blank" rel="noreferrer">County index ↗</a>}
      </div>

      {!protocolStorageSupported() && <div className="sac-protocol-alert">This browser does not provide the local database required for offline protocol storage. Online lookup can still work, but protocol documents cannot be saved for offline use.</div>}

      {syncing && (
        <div className="sac-protocol-sync">
          <div><strong>Preparing offline library</strong><span>{sync.completed} of {sync.total} checked · {sync.downloaded} new · {sync.failed} failed</span></div>
          <progress max={Math.max(1, sync.total)} value={sync.completed} />
        </div>
      )}
      {sync.state === 'partial' && <div className="sac-protocol-alert">{sync.failed} document{sync.failed === 1 ? '' : 's'} could not be refreshed. Saved copies remain available where present. <button type="button" onClick={manualSync}>Retry</button></div>}

      <div className="sac-protocol-controls">
        <label>
          <span>Search protocols</span>
          <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="8031, cardiac arrest, burns, airway…" autoComplete="off" />
        </label>
        <label>
          <span>Category</span>
          <select value={category} onChange={event => setCategory(event.target.value)}>
            {categories.map(item => <option key={item}>{item}</option>)}
          </select>
        </label>
        {!syncing && onlineNow() && total > 0 && <button className="sac-protocol-refresh" type="button" onClick={manualSync}>Refresh Offline Library</button>}
      </div>

      <div className="sac-protocol-result-count">{results.length} document{results.length === 1 ? '' : 's'}{terms.length ? ` matching “${query.trim()}”` : ''}</div>

      <div className="sac-protocol-list">
        {results.map(protocol => {
          const cached = cachedUrls.has(protocol.url);
          return (
            <button type="button" className="sac-protocol-row" key={protocol.id || protocol.url} onClick={() => openProtocol(protocol)}>
              <span className="sac-protocol-number">{displayProtocolNumber(protocol) || 'EMS'}</span>
              <span className="sac-protocol-title"><strong>{protocol.title}</strong><small>{protocol.category}{protocol.revision && protocol.revision !== protocol.number ? ` · Rev ${protocol.revision}` : ''}</small></span>
              <span className={`sac-protocol-offline ${cached ? 'cached' : ''}`}>{cached ? 'OFFLINE ✓' : onlineNow() ? 'CACHING' : 'NOT SAVED'}</span>
              <span className="sac-protocol-open">Open →</span>
            </button>
          );
        })}
        {!results.length && status.state !== 'loading' && <div className="sac-protocol-empty">No Sacramento County EMS documents match your search.</div>}
        {status.state === 'loading' && <div className="sac-protocol-empty">Loading saved protocol index…</div>}
      </div>

      <p className="sac-protocol-footnote">Field reference only. Official Sacramento County EMS documents are rendered directly in the app without rewriting treatment content. When service is available, the County source is checked automatically for updated document links.</p>
    </div>
  );
}
